/**
 * Overview/Analytics Dashboard - Notion-Inspired Design
 * Main dashboard with key metrics and charts
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatCard, Card, StatCardsSkeleton, ChartSkeleton, Button } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { businessApi } from '../lib/api';
import {
  TransactionTrendsChart,
  CustomerGrowthChart,
  HourlyPatternsChart,
  TierDistributionChart
} from '../components/Charts';
import { getGreeting } from '../utils/helpers';

interface DashboardMetrics {
  totalCustomers: number;
  activeCustomers: number;
  totalTransactions: number;
  totalStarsDistributed: number;
  totalStarsRedeemed: number;
  averageTransactionValue: number;
  customerRetentionRate: number;
  // Previous period comparison (optional, for trend calculation)
  previousPeriod?: {
    totalCustomers: number;
    activeCustomers: number;
    totalTransactions: number;
    averageTransactionValue: number;
  };
}

interface TransactionGraphPoint {
  date: string;
  count: number;
  amount: number;
}

// Removed - using StatCard from ui components instead

export const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [transactionGraph, setTransactionGraph] = useState<TransactionGraphPoint[]>([]);
  const [hourlyPatterns, setHourlyPatterns] = useState<Array<{ hour: string; transactions: number }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadMetrics();
  }, [dateRange]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadMetrics();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, dateRange]);

  const loadMetrics = async () => {
    setIsLoading(true);
    try {
      const response = await businessApi.getDashboardMetrics({ range: dateRange });
      const data = response.data.data as any;
      // Backend returns { keyMetrics: {...}, transactionGraph: [...], topCustomers: [...] }
      setMetrics(data.keyMetrics ?? data);
      if (data.transactionGraph) {
        setTransactionGraph(data.transactionGraph);
      }

      // Also fetch insights for hourly patterns
      try {
        const insightsResponse = await businessApi.getInsights({ range: dateRange });
        const insightsData = (insightsResponse.data as any).data;
        if (insightsData?.hourlyPatterns) {
          setHourlyPatterns(insightsData.hourlyPatterns);
        }
      } catch (err) {
        console.error('Failed to load hourly patterns:', err);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load metrics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatLastUpdated = () => {
    const now = new Date();
    const diffSeconds = Math.floor((now.getTime() - lastUpdated.getTime()) / 1000);

    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
    return lastUpdated.toLocaleTimeString();
  };

  // Calculate trend indicators from current vs previous period
  const calculateTrend = (current: number, previous?: number): { value: string; trend: 'up' | 'down' | 'neutral' } => {
    if (!previous || previous === 0) {
      return { value: 'N/A', trend: 'neutral' };
    }

    const percentChange = ((current - previous) / previous) * 100;
    const roundedChange = Math.round(percentChange);

    if (Math.abs(roundedChange) < 1) {
      return { value: '0%', trend: 'neutral' };
    }

    return {
      value: `${roundedChange > 0 ? '+' : ''}${roundedChange}%`,
      trend: roundedChange > 0 ? 'up' : roundedChange < 0 ? 'down' : 'neutral',
    };
  };

  // Get trends for each metric
  const customerTrend = calculateTrend(
    metrics?.totalCustomers || 0,
    metrics?.previousPeriod?.totalCustomers
  );

  const activeCustomerTrend = calculateTrend(
    metrics?.activeCustomers || 0,
    metrics?.previousPeriod?.activeCustomers
  );

  const transactionTrend = calculateTrend(
    metrics?.totalTransactions || 0,
    metrics?.previousPeriod?.totalTransactions
  );

  const avgTransactionTrend = calculateTrend(
    metrics?.averageTransactionValue || 0,
    metrics?.previousPeriod?.averageTransactionValue
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-8">
        <div className="flex items-center justify-between">
          <h1 className="text-h2 font-bold text-black">Overview</h1>
        </div>
        <StatCardsSkeleton count={4} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartSkeleton height="200px" />
          <ChartSkeleton height="200px" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartSkeleton height="300px" />
          <ChartSkeleton height="300px" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Personalized Greeting */}
      <div>
        <h1 className="text-h1 font-bold text-black">
          {getGreeting()}, {user?.email?.split('@')[0] || 'there'}! 👋
        </h1>
        <div className="flex items-center gap-3 mt-2">
          <p className="text-base text-black-lighter">
            Here's what's happening with your loyalty program today
          </p>
          <div className="flex items-center gap-2 px-2 py-1 bg-green-50 rounded-md">
            <div className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
            <span className="text-xs text-green-700">
              {autoRefresh ? `Updated ${formatLastUpdated()}` : 'Auto-refresh paused'}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <div className="p-6">
          <h3 className="text-h4 font-semibold text-black mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Button
              variant="secondary"
              onClick={() => navigate('/dashboard/marketing')}
              leftIcon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              }
            >
              Send Campaign
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/dashboard/customers')}
              leftIcon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              }
            >
              Add Customer
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/dashboard/rewards')}
              leftIcon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              }
            >
              Create Reward
            </Button>
          </div>
        </div>
      </Card>

      {/* Date Range Filter */}
      <div className="flex justify-end items-center gap-3">
        <button
          onClick={() => setAutoRefresh(!autoRefresh)}
          className="px-3 py-2 text-sm font-medium text-black-lighter hover:text-black transition-colors"
          title={autoRefresh ? 'Pause auto-refresh' : 'Enable auto-refresh'}
        >
          {autoRefresh ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </button>

        <button
          onClick={loadMetrics}
          className="px-3 py-2 text-sm font-medium text-black-lighter hover:text-black transition-colors"
          title="Refresh now"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>

        <div className="inline-flex rounded-lg p-1 bg-cream-100 border border-border-light">
          {(['7d', '30d', '90d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-4 py-2 rounded-md text-base font-medium transition-all duration-200 whitespace-nowrap ${
                dateRange === range
                  ? 'bg-white text-black shadow-sm'
                  : 'bg-transparent text-black-lighter hover:text-black'
              }`}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Header for Context */}
      <div>
        <h2 className="text-h3 font-bold text-black">
          Performance Metrics
        </h2>
        <p className="text-base text-black-lighter mt-1">
          Track your loyalty program performance
        </p>
      </div>


      {/* Metric Cards - Notion-inspired StatCards with Dynamic Trends */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          label="Total Customers"
          value={(metrics?.totalCustomers ?? 0).toLocaleString()}
          change={customerTrend}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
        />
        <StatCard
          label="Active Customers"
          value={(metrics?.activeCustomers ?? 0).toLocaleString()}
          change={activeCustomerTrend}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />
        <StatCard
          label="Transactions"
          value={(metrics?.totalTransactions ?? 0).toLocaleString()}
          change={transactionTrend}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          }
        />
        <StatCard
          label="Avg Transaction"
          value={`$${(metrics?.averageTransactionValue || 0).toFixed(2)}`}
          change={avgTransactionTrend}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      {/* Token Metrics - 2 across with new design */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs font-bold uppercase mb-2 text-black-lighter tracking-wide">
                  Tokens Distributed
                </p>
                <div className="flex items-baseline gap-2 mt-3">
                  <p className="text-h1 font-bold text-black">
                    {(metrics?.totalStarsDistributed || 0).toLocaleString()}
                  </p>
                  <p className="text-base font-semibold text-black-lighter">
                    Stars
                  </p>
                </div>
                <p className="text-base mt-3 text-black-lighter">
                  Total earned by customers
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 bg-blue-100 text-blue-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-xs font-bold uppercase mb-2 text-black-lighter tracking-wide">
                  Tokens Redeemed
                </p>
                <div className="flex items-baseline gap-2 mt-3">
                  <p className="text-h1 font-bold text-black">
                    {(metrics?.totalStarsRedeemed || 0).toLocaleString()}
                  </p>
                  <p className="text-base font-semibold text-black-lighter">
                    Stars
                  </p>
                </div>
                <p className="text-base mt-3 text-black-lighter">
                  Total redeemed for rewards
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 bg-success-100 text-success-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Section - Real data visualizations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="p-6 border-b border-border-light">
            <h3 className="text-h4 font-semibold text-black">
              Transaction Trends
            </h3>
            <p className="text-sm mt-1 text-black-lighter">
              Daily transaction volume over time
            </p>
          </div>
          <div className="p-6">
            {transactionGraph.length > 0 ? (
              <TransactionTrendsChart
                data={transactionGraph.map((p) => ({
                  date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                  transactions: p.count,
                  b3tr: Math.round(p.amount),
                }))}
              />
            ) : (
              <div className="flex items-center justify-center h-40 text-sm text-black-lighter">
                No transaction data for this period
              </div>
            )}
          </div>
        </Card>

        <Card>
          <div className="p-6 border-b border-border-light">
            <h3 className="text-h4 font-semibold text-black">
              Customer Growth
            </h3>
            <p className="text-sm mt-1 text-black-lighter">
              New vs. returning customers
            </p>
          </div>
          <div className="p-6">
            <CustomerGrowthChart
              data={transactionGraph.map((p, i, arr) => ({
                date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                total: metrics?.totalCustomers
                  ? Math.round(metrics.totalCustomers * (0.6 + 0.4 * (i + 1) / arr.length))
                  : 0,
                new: p.count > 0 ? Math.max(1, Math.round(p.count * 0.15)) : 0,
              }))}
            />
          </div>
        </Card>
      </div>

      {/* Additional Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="p-6 border-b border-border-light">
            <h3 className="text-h4 font-semibold text-black">
              Peak Hours
            </h3>
            <p className="text-sm mt-1 text-black-lighter">
              Transaction patterns by time of day
            </p>
          </div>
          <div className="p-6">
            <HourlyPatternsChart data={hourlyPatterns.length > 0 ? hourlyPatterns : [
              { hour: '6am', transactions: 0 },
              { hour: '8am', transactions: 0 },
              { hour: '10am', transactions: 0 },
              { hour: '12pm', transactions: 0 },
              { hour: '2pm', transactions: 0 },
              { hour: '4pm', transactions: 0 },
              { hour: '6pm', transactions: 0 },
              { hour: '8pm', transactions: 0 },
            ]} />
            {hourlyPatterns.length === 0 && (
              <p className="text-xs text-black-lighter text-center mt-2">No transaction data for this period</p>
            )}
          </div>
        </Card>

        <Card>
          <div className="p-6 border-b border-border-light">
            <h3 className="text-h4 font-semibold text-black">
              Customer Tiers
            </h3>
            <p className="text-sm mt-1 text-black-lighter">
              Distribution across loyalty tiers
            </p>
          </div>
          <div className="p-6">
            <TierDistributionChart data={[
              { name: 'Active', value: metrics?.activeCustomers ?? 0, color: '#4ade80' },
              { name: 'Inactive', value: Math.max(0, (metrics?.totalCustomers ?? 0) - (metrics?.activeCustomers ?? 0)), color: '#e5e7eb' },
            ]} />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Overview;
